export interface BulkDiscountTier {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  amountOff?: number | null;
  percentOff?: number | null;
}

export interface VariantOptionTier extends BulkDiscountTier {
  quantityCondition?: string | null;
  variantOptions?: unknown[] | null;
}

export interface BulkDiscountOptions {
  /** Floor a percentOff tier on one unit's price and multiply back, as ticket tiers are priced. */
  perUnit?: boolean;
}

export type AppliedBulkDiscount<T> = T & { amount: number };

export interface BulkDiscountResult<T> {
  bulkDiscount: AppliedBulkDiscount<T> | null;
  bulkDiscountAmount: number;
}

export interface TieredOptionLine<T> {
  unitPrice: number;
  lineAmount: number;
  discountAmount: number;
  bulkDiscount: AppliedBulkDiscount<T> | null;
}

function selectTier<T extends BulkDiscountTier>(
  bulkDiscounts: T[] | null | undefined,
  quantity: number,
): T | null {
  let selected: T | null = null;

  for (const tier of bulkDiscounts ?? []) {
    const applies =
      quantity >= Number(tier.minQuantity) &&
      (tier.maxQuantity ? quantity <= tier.maxQuantity : true);

    if (applies && (!selected || Number(tier.minQuantity) > Number(selected.minQuantity))) {
      selected = tier;
    }
  }

  return selected;
}

/** The tier's discount before the cap at the subtotal. */
export function getBulkDiscountTierAmount(
  tier: BulkDiscountTier,
  quantity: number,
  subtotal: number,
  { perUnit = false }: BulkDiscountOptions = {},
): number {
  if (tier.amountOff) {
    return Math.floor(tier.amountOff * quantity);
  }

  const rate = (tier.percentOff ?? 0) / 100;

  if (perUnit) {
    return quantity > 0 ? Math.floor((subtotal / quantity) * rate) * quantity : 0;
  }

  return Math.floor(subtotal * rate);
}

export function getBulkDiscount<T extends BulkDiscountTier>(
  bulkDiscounts: T[] | null | undefined,
  quantity: number,
  subtotal: number,
  options: BulkDiscountOptions = {},
): BulkDiscountResult<T> {
  const tier = selectTier(bulkDiscounts, quantity);

  if (!tier) {
    return { bulkDiscount: null, bulkDiscountAmount: 0 };
  }

  const amount = Math.min(getBulkDiscountTierAmount(tier, quantity, subtotal, options), subtotal);

  return { bulkDiscount: { ...tier, amount }, bulkDiscountAmount: amount };
}

export function getBulkDiscountsForVariantOption<T extends VariantOptionTier>(
  bulkDiscounts: T[] | null | undefined,
  variantOptionId: unknown,
): T[] {
  if (!bulkDiscounts?.length || variantOptionId == null) return [];

  const id = String(variantOptionId);

  return bulkDiscounts.filter(
    tier =>
      tier.quantityCondition === 'variantOptionQuantity' &&
      (tier.variantOptions ?? []).some(optionId => optionId != null && String(optionId) === id),
  );
}

/**
 * A variant option under its own tier is sold at a whole minor-unit price, so the line is that
 * price times the quantity and the discount is what that leaves off the undiscounted line.
 */
export function getTieredOptionLine<T extends BulkDiscountTier>({
  unitPrice,
  quantity,
  bulkDiscounts,
}: {
  unitPrice: number;
  quantity: number;
  bulkDiscounts?: T[] | null;
}): TieredOptionLine<T> {
  if (!(quantity > 0)) {
    return { unitPrice: 0, lineAmount: 0, discountAmount: 0, bulkDiscount: null };
  }

  const subtotal = unitPrice * quantity;
  const { bulkDiscount, bulkDiscountAmount } = getBulkDiscount(bulkDiscounts, quantity, subtotal);

  if (!bulkDiscount) {
    return { unitPrice, lineAmount: subtotal, discountAmount: 0, bulkDiscount: null };
  }

  const discountedUnitPrice = Math.round((subtotal - bulkDiscountAmount) / quantity);
  const lineAmount = discountedUnitPrice * quantity;
  const discountAmount = subtotal - lineAmount;

  return {
    unitPrice: discountedUnitPrice,
    lineAmount,
    discountAmount,
    bulkDiscount: { ...bulkDiscount, amount: discountAmount },
  };
}
